import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { KeyRound, CheckCircle } from 'lucide-react';

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const isValid = useMemo(() => password.length >= 8 && password === confirm, [password, confirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setStatus('loading');
    try {
      await api('/auth/set-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Failed');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
        <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)] text-center">
          <CardHeader className="pb-4">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle className="h-6 w-6" />
            </div>
            <CardTitle>All set!</CardTitle>
            <CardDescription>Your password has been set successfully.</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/login" className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity">Go to login</a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
      <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)]">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
          <CardDescription className="text-center">Set up your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Email</Label>
              <Input type="email" value={email} disabled className="bg-[var(--color-muted)] text-[var(--color-muted-foreground)]" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" minLength={8} placeholder="Min. 8 characters" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Confirm Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="pl-10" placeholder="Repeat password" required />
              </div>
              {password && confirm && password !== confirm && (
                <p className="text-xs text-[var(--color-destructive)]">Passwords do not match</p>
              )}
            </div>
            <Button type="submit" disabled={!isValid || status === 'loading'} loading={status === 'loading'} className="w-full">
              {status === 'loading' ? 'Setting password...' : 'Set Password'}
            </Button>
            {status === 'error' && (
              <Badge variant="destructive" className="w-full justify-center py-1.5 text-xs font-medium rounded-md">{error}</Badge>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
